import React, { useState, useEffect } from 'react';
import { api, setToken } from './utils/api';
import { formatSignedUsd } from './utils/numberFormat';
import { DashboardPage } from './pages/DashboardPage';
import { RouterPage } from './pages/RouterPage';
import { SettingsPage } from './pages/SettingsPage';
import { TokenRiskPage } from './pages/TokenRiskPage';
import { AuditPage } from './pages/AuditPage';
import { AutoBalancePage } from './pages/AutoBalancePage';
import { BalanceHistoryDetails } from './components/BalanceHistoryDetails';

type Tab = 'dashboard' | 'router' | 'autoBalance' | 'settings' | 'risk' | 'audit';
const summaryPeriods = [
  { key: 'today', label: 'сегодня' },
  { key: 'yesterday', label: 'вчера' },
  { key: '3d', label: '3д' },
  { key: '7d', label: '7д' },
  { key: '14d', label: '14д' },
  { key: '21d', label: '21д' },
  { key: '30d', label: '30д' },
] as const;

function emptySummaryStat() {
  return { tradeCount: 0, totalProfitUsd: 0 };
}

function summaryTitle(stats: any): string {
  return summaryPeriods
    .map(period => {
      const bucket = stats?.[period.key] || emptySummaryStat();
      return period.label + ': сделок ' + bucket.tradeCount + ', итог ' + formatSignedUsd(bucket.totalProfitUsd);
    })
    .join(' · ');
}

function getInitialTheme(): string {
  const saved = localStorage.getItem('theme');
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export default function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sysStatus, setSysStatus] = useState<any>(null);
  const [scannerBusy, setScannerBusy] = useState(false);
  const [scannerActionError, setScannerActionError] = useState('');
  const [tradeSummaryStats, setTradeSummaryStats] = useState<any>(null);
  const [balanceHistoryOpen, setBalanceHistoryOpen] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const status = await api.authStatus();
        if (cancelled) return;
        if (status.authenticated) {
          setLoggedIn(true);
          fetchStatus();
        }
      } catch {
        // Login form will fetch captcha below when auth status is unavailable.
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    }

    checkAuth();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authChecked && !loggedIn) fetchCaptcha();
  }, [authChecked, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    fetchStatus();
    const timer = window.setInterval(fetchStatus, 2000);
    return () => window.clearInterval(timer);
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;

    async function fetchTradeSummaryStats() {
      try {
        const stats = await api.getTradeSummaryStats();
        if (!cancelled) setTradeSummaryStats(stats);
      } catch {}
    }

    fetchTradeSummaryStats();
    const timer = window.setInterval(fetchTradeSummaryStats, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [loggedIn]);

  async function fetchStatus() {
    try {
      const s = await api.getSystemStatus();
      setSysStatus(s);
    } catch {}
  }

  async function fetchCaptcha() {
    try {
      const captcha = await api.getCaptcha();
      setCaptchaQuestion(captcha.question + ' = ?');
      setCaptchaId(captcha.id);
    } catch {}
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginError('');
    try {
      const result = await api.login(username, password, captchaId, captchaValue);
      setToken(result.token);
      setLoggedIn(true);
      fetchStatus();
    } catch (err: any) {
      setLoginError(err.message || 'Ошибка входа');
      fetchCaptcha();
    }
  }

  function handleLogout() {
    setToken(null);
    setLoggedIn(false);
    api.logout().catch(() => {});
  }

  async function handleToggleScanner() {
    if (scannerBusy || !sysStatus) return;
    setScannerBusy(true);
    setScannerActionError('');
    try {
      const scannerStatus: any = sysStatus.scannerRunning
        ? await api.stopScanner()
        : await api.startScanner();
      setSysStatus((prev: any) => prev ? { ...prev, scannerRunning: !!scannerStatus.running } : prev);
      await fetchStatus();
    } catch (err: any) {
      setScannerActionError(err?.message || 'Не удалось переключить сканер');
    } finally {
      setScannerBusy(false);
    }
  }

  if (!loggedIn) {
    return (
      <div className="login-page">
        <form className="login-form" onSubmit={handleLogin}>
          <h2>TON Arb v2</h2>
          <input
            type="text" placeholder="Логин" value={username}
            onChange={e => setUsername(e.target.value)}
          />
          <input
            type="password" placeholder="Пароль" value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {captchaQuestion && (
            <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
              <span className="text-mono text-sm">{captchaQuestion}</span>
              <input
                type="number" placeholder="= ?" value={captchaValue}
                onChange={e => setCaptchaValue(e.target.value)}
                style={{ width: 80, padding: '4px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-primary)', fontSize: 12 }}
              />
            </div>
          )}
          {loginError && <p style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 12 }}>{loginError}</p>}
          <button className="btn btn-primary" type="submit" style={{ width: '100%' }}>Войти</button>
        </form>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Главная' },
    { id: 'router', label: 'Роутер' },
    { id: 'autoBalance', label: 'Автобаланс' },
    { id: 'settings', label: 'Настройки' },
    { id: 'risk', label: 'Риски' },
    { id: 'audit', label: 'Аудит' },
  ];

  return (
    <div className="app">
      <header className="header">
        <div className="header-main">
          <h1>TON Arb v2</h1>
          <nav className="tabs" aria-label="Основная навигация">
            {tabs.map(t => (
              <button
                type="button"
                key={t.id} className={'tab' + (tab === t.id ? ' active' : '')}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <div className="header-trade-summary" title={summaryTitle(tradeSummaryStats)}>
            {summaryPeriods.map(period => {
              const bucket = tradeSummaryStats?.[period.key] || emptySummaryStat();
              const profitClass = bucket.totalProfitUsd > 0
                ? 'positive'
                : bucket.totalProfitUsd < 0
                  ? 'negative'
                  : 'neutral';
              return (
                <span key={period.key} className="header-trade-summary-item">
                  <span className="header-trade-summary-period">{period.label}</span>
                  <span className="header-trade-summary-count">{bucket.tradeCount}</span>
                  <span className={'header-trade-summary-profit ' + profitClass}>{formatSignedUsd(bucket.totalProfitUsd)}</span>
                </span>
              );
            })}
            <button type="button" className="header-summary-details" onClick={() => setBalanceHistoryOpen(true)}>Подробнее</button>
          </div>
        </div>
        <div className="header-actions">
          {sysStatus && (
            <>
              <button
                type="button"
                className={'scanner-toggle-btn ' + (sysStatus.scannerRunning ? 'active' : 'inactive')}
                onClick={handleToggleScanner}
                disabled={scannerBusy}
                title={sysStatus.scannerRunning ? 'Остановить сканер' : 'Запустить сканер'}
              >
                <span className="scanner-toggle-dot" />
                {scannerBusy ? 'Сканер...' : 'Сканер ' + (sysStatus.scannerRunning ? 'ВКЛ' : 'ВЫКЛ')}
              </button>
              <span className="header-status">
                Live: {sysStatus.liveEnabled ? 'ВКЛ' : 'ВЫКЛ'}
              </span>
            </>
          )}
          {scannerActionError && <span className="header-error">{scannerActionError}</span>}
          <button className="theme-btn" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? 'Светлая' : 'Тёмная'}
          </button>
          <button className="btn btn-sm btn-outline" onClick={handleLogout}>Выйти</button>
        </div>
      </header>

      <main className="content">
        {tab === 'dashboard' && <DashboardPage />}
        {tab === 'router' && <RouterPage />}
        {tab === 'autoBalance' && <AutoBalancePage />}
        {tab === 'settings' && <SettingsPage />}
        {tab === 'risk' && <TokenRiskPage />}
        {tab === 'audit' && <AuditPage />}
      </main>
      {balanceHistoryOpen && <BalanceHistoryDetails onClose={() => setBalanceHistoryOpen(false)} />}
    </div>
  );
}
